'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCitation,
  EMPTY_INPUT,
  splitItalics,
  toPlainText,
  type CitationInput,
  type CitationStyle,
  type SourceType,
} from '@/lib/citation-tools';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type CitationVariant =
  | 'citation-generator'
  | 'apa-citation-generator'
  | 'mla-citation-generator';

const SOURCE_LABELS: Array<{ value: SourceType; label: string }> = [
  { value: 'website', label: 'Website or web page' },
  { value: 'book', label: 'Book' },
  { value: 'journal', label: 'Journal article' },
];

function Rendered({ value }: { value: string }) {
  return (
    <span>
      {splitItalics(value).map((part, index) =>
        part.italic ? <em key={index}>{part.text}</em> : <span key={index}>{part.text}</span>,
      )}
    </span>
  );
}

export default function BrowserCitationGenerator({ variant }: { variant: CitationVariant }) {
  const usageReported = useRef(false);
  const locked: CitationStyle | null =
    variant === 'apa-citation-generator'
      ? 'apa'
      : variant === 'mla-citation-generator'
        ? 'mla'
        : null;

  const [style, setStyle] = useState<CitationStyle>(locked ?? 'apa');
  const [input, setInput] = useState<CitationInput>(EMPTY_INPUT);
  const [list, setList] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const citation = useMemo(() => buildCitation(input, style), [input, style]);
  const hasContent = Boolean(input.title.trim() || input.authors.some((a) => a.family.trim()));

  useEffect(() => {
    if (!usageReported.current && hasContent) {
      usageReported.current = true;
      void consumeToolUsage(variant, '').catch(() => undefined);
    }
  }, [hasContent, variant]);

  function set<K extends keyof CitationInput>(key: K, value: CitationInput[K]) {
    setInput((prev) => ({ ...prev, [key]: value }));
  }

  function setAuthor(index: number, field: 'given' | 'family', value: string) {
    setInput((prev) => {
      const authors = prev.authors.map((author, i) =>
        i === index ? { ...author, [field]: value } : author,
      );
      return { ...prev, authors };
    });
  }

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(toPlainText(text));
      setCopied(key);
      setTimeout(() => setCopied((current) => (current === key ? null : current)), 1500);
    } catch {
      setCopied(null);
    }
  }

  const containerLabel =
    input.type === 'book'
      ? 'Publisher'
      : input.type === 'journal'
        ? 'Journal name'
        : 'Website name';

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>
            {locked === 'apa'
              ? 'APA 7 citation'
              : locked === 'mla'
                ? 'MLA 9 citation'
                : 'Build a citation'}
          </h2>
          <p>
            Fill in what you know and the citation formats itself as you type. Everything runs in
            your browser, so nothing you paste is uploaded.
          </p>
        </div>
        <span className="tool-live-badge">{locked ? locked.toUpperCase() : 'APA / MLA'}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ alignItems: 'stretch', gap: '0.75rem' }}>
          <label>
            Source type
            <select
              onChange={(event) => set('type', event.target.value as SourceType)}
              value={input.type}
            >
              {SOURCE_LABELS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {input.authors.map((author, index) => (
            <div key={index} style={{ display: 'flex', gap: '0.5rem' }}>
              <label style={{ flex: 1 }}>
                {index === 0 ? 'Author last name' : `Author ${index + 1} last name`}
                <input
                  onChange={(event) => setAuthor(index, 'family', event.target.value)}
                  placeholder="Austen"
                  type="text"
                  value={author.family}
                />
              </label>
              <label style={{ flex: 1 }}>
                First name
                <input
                  onChange={(event) => setAuthor(index, 'given', event.target.value)}
                  placeholder="Jane"
                  type="text"
                  value={author.given}
                />
              </label>
            </div>
          ))}

          <button
            className="button button-ghost"
            onClick={() => set('authors', [...input.authors, { given: '', family: '' }])}
            type="button"
          >
            Add another author
          </button>

          <label>
            Title
            <input
              onChange={(event) => set('title', event.target.value)}
              placeholder="Pride and Prejudice"
              type="text"
              value={input.title}
            />
          </label>

          <label>
            {containerLabel}
            <input
              onChange={(event) => set('container', event.target.value)}
              type="text"
              value={input.container}
            />
          </label>

          <label>
            Year
            <input
              inputMode="numeric"
              onChange={(event) => set('year', event.target.value)}
              placeholder="2020"
              type="text"
              value={input.year}
            />
          </label>

          {input.type === 'journal' ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <label style={{ flex: 1 }}>
                Volume
                <input
                  onChange={(event) => set('volume', event.target.value)}
                  type="text"
                  value={input.volume}
                />
              </label>
              <label style={{ flex: 1 }}>
                Issue
                <input
                  onChange={(event) => set('issue', event.target.value)}
                  type="text"
                  value={input.issue}
                />
              </label>
              <label style={{ flex: 1 }}>
                Pages
                <input
                  onChange={(event) => set('pages', event.target.value)}
                  placeholder="112-130"
                  type="text"
                  value={input.pages}
                />
              </label>
            </div>
          ) : null}

          {input.type === 'journal' ? (
            <label>
              DOI
              <input
                onChange={(event) => set('doi', event.target.value)}
                placeholder="10.1111/jsr.12987"
                type="text"
                value={input.doi}
              />
            </label>
          ) : (
            <label>
              URL
              <input
                onChange={(event) => set('url', event.target.value)}
                placeholder="https://example.com/page"
                type="text"
                value={input.url}
              />
            </label>
          )}

          {input.type === 'website' ? (
            <label>
              Accessed date (MLA)
              <input
                onChange={(event) => set('accessed', event.target.value)}
                placeholder="2 Aug. 2026"
                type="text"
                value={input.accessed}
              />
            </label>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          {locked ? null : (
            <label>
              Style
              <select
                onChange={(event) => setStyle(event.target.value as CitationStyle)}
                value={style}
              >
                <option value="apa">APA 7th edition</option>
                <option value="mla">MLA 9th edition</option>
              </select>
            </label>
          )}

          <div
            aria-live="polite"
            className="tool-live-preview"
            style={{
              border: '1px solid var(--border, #3a4152)',
              borderRadius: 8,
              lineHeight: 1.6,
              minHeight: 90,
              padding: '0.75rem',
            }}
          >
            {hasContent ? <Rendered value={citation} /> : 'Your citation appears here as you type.'}
          </div>

          {hasContent ? (
            <>
              <button
                className="button button-ghost"
                onClick={() => void copy(citation, 'one')}
                type="button"
              >
                {copied === 'one' ? 'Copied!' : 'Copy this citation'}
              </button>
              <button
                className="button button-ghost"
                onClick={() => setList((prev) => [...prev, citation])}
                type="button"
              >
                Add to bibliography
              </button>
            </>
          ) : null}

          {list.length ? (
            <>
              <strong>Bibliography ({list.length})</strong>
              <ol style={{ display: 'grid', gap: '0.4rem', paddingLeft: '1.1rem' }}>
                {list.map((entry, index) => (
                  <li key={index} style={{ lineHeight: 1.5 }}>
                    <Rendered value={entry} />
                  </li>
                ))}
              </ol>
              <button
                className="button button-ghost"
                onClick={() => void copy(list.join('\n\n'), 'all')}
                type="button"
              >
                {copied === 'all' ? 'Copied!' : 'Copy the whole list'}
              </button>
            </>
          ) : null}

          <p className="tool-live-panel-note">
            Copying puts plain text on the clipboard, so italics are lost. Italicize the titles
            shown in italics above once the citation is in your document, and check the result
            against the style guide your institution follows.
          </p>
        </aside>
      </div>
    </article>
  );
}
